import { AppBase } from '@jollywise/jollygoodgame';
import { KEYS, SCENE_MAP } from './app/constants/SceneConstants';
import PlayerModel from './app/model/PlayerModel';
import { FONTS } from './app/constants/AppFonts';
import { GAME_MODES } from './app/constants/Constants';
import { HudConfig } from './app/constants/HudConfig';
import ShortcutController from './app/shortcuts/ShortcutController';
import { GameModel } from './app/model/GameModel';
import { LoadAPIData } from './app/utils/Utils';
import { GAME_EVENTS } from '@jollywise/jollygoodgame/src/constants/Events';
import { KeyMap } from './app/model/KeyMap';

export default class App extends AppBase {
  constructor(opts) {
    super(opts);

    this._appMode = GAME_MODES.STORY;
    this._language = opts.config.locale;
    this._isDesktop = opts.config.forceTouch ? false : this.device.os.desktop;
    this._urlParameters = opts.config.urlParameters;
    this.events.once(Phaser.Core.Events.READY, this.bootUp, this);
    window.app = this;

    setInterval(() => !document.hasFocus() && window.focus(), 100);
  }

  get locale() {
    return this._language;
  }

  bootUp() {
    this.hud.setHudConfig(HudConfig);
    const assetPack = require('../assets/assetpack.json');
    assetPack.locale.path = 'assets/locale/' + this._language;

    this.loader.bootGame(
      {
        boot: {
          fonts: FONTS.BOOT,
          assetpacks: [{ config: assetPack, keys: ['boot', 'boot_ui', 'locale'] }],
        },
        load: {
          assetpacks: [],
        },
      },
      () => {
        const config = this.cache.json.get('gameconfig').result;
        this.cache.json.remove('gameconfig');
        this.cache.json.add('gameconfig', config);
        assetPack.maps = {
          path: 'assets/maps/',
          files: this.cache.json.get('gameconfig').levels.reduce((arr, level) => {
            arr.push(
              ...level.data.stages.map((s) => ({
                type: 'json',
                key: s.mapid,
                url: s.mapid + '.json',
              }))
            );
            return arr;
          }, []),
        };

        this.loader.loadAssetPack(
          assetPack,
          ['ui', 'spine', 'maps', 'game', 'music', 'audiosprites'],
          this.loadFromCMS.bind(this)
        );
      }
    );
  }

  get player() {
    return this.playerModel;
  }

  get isDesktop() {
    return this._isDesktop;
  }

  assetsLoaded() {
    super.assetsLoaded();
    this.sceneController.addSceneMap(SCENE_MAP);
    this.copy.addCopyFromJSON(this.cache.json.get('copy'));
    this.copy.setLocale(this._language);
    this.model = new GameModel(this.cache.json.get('gameconfig'));
    this.keymap = new KeyMap(this, this.cache.json.get('controls'));
    this.playerModel = new PlayerModel(this, {
      freeHeroes: this.model.getFreeHeroes(),
    });
    if (__DEBUG__) this._shortcuts = new ShortcutController(this);
    this._setHudEvents();
    this.showWelcome();

    if (this._urlParameters.get('combattest')) {
      this.sceneController.switchScene(KEYS.CombatTest, {});
    } else if (this._urlParameters.get('charactertest')) {
      this.sceneController.switchScene(KEYS.EnemyViewer, {});
    } else if (__WATCH__) {
      this.quickStart('beastboy');
      // this.sceneController.switchScene(KEYS.CombatTest, {});
      // this.showEnemyViewer();
      // this.showUnlocks();
      // this.testStage({ stageid: '1-1' });
      // this.showIntro();
    }

    if (this._urlParameters.get('shortcuts')) this.shortcuts.show();

    this.events.on(GAME_EVENTS.PAUSED, this.handleGamePaused, this);
  }

  quickStart(character) {
    this.player.setActiveHero(character);
    this.player.startNewStory();
    this.playCurrentStoryStage();
  }

  showWelcome() {
    this.sceneController.switchScene(KEYS.Welcome, { data: {} });
  }

  showIntro() {
    this.sceneController.switchScene(KEYS.CutScene, {
      data: { type: 'intro', onComplete: this.playCurrentStoryStage.bind(this) },
    });
  }

  showUnlocks() {
    // this.sceneController.switchScene(KEYS.ItemUnlocks, { data: {} });
    this.sceneController.showOverlayScene(KEYS.ItemUnlocks, { data: {} });
  }

  showCharacterSelect() {
    this.sceneController.switchScene(KEYS.CharacterSelect, { data: {} });
  }

  startNewStoryGame() {
    if (this.player.hasStoryProgress()) {
      this.sceneController.showOverlayScene(KEYS.ConfirmAction, {
        data: {
          type: 'newgame',
          onConfirm: () => {
            this.player.startNewStory();
            this.showIntro();
          },
          onConfirmScope: this,
        },
      });
    } else {
      this.player.startNewStory();
      this.showIntro();
    }
  }
  resumeSavedStoryGame() {
    this.playCurrentStoryStage();
  }

  playCurrentStoryStage() {
    this._validateStoryStart();
    this.sceneController.switchScene(KEYS.GamePlay, {
      data: {
        stageid: this.player.story.getCurrentStageId(),
        heroid: this.player.activeHero,
      },
    });
  }

  startFromStage({ stageid }) {
    this.player.startNewStory();
    this.player.jumpToStoryStage(stageid);
    this.playCurrentStoryStage();
  }

  testStage({ stageid }) {
    this._appMode = GAME_MODES.STAGE_TEST;
    this.sceneController.switchScene(KEYS.GamePlay, {
      data: { stageid, heroid: this.player.activeHero || 'robin' },
    });
  }

  completeStage(results = {}) {
    this.setShortcuts(ShortcutController.STATES.DEFAULT);
    if (this._appMode === GAME_MODES.STAGE_TEST) {
      const calulcatedResults = this.model.calculateLevelResults(results);
      const isHighScore = this.player.registerScore(
        this.player.story.getCurrentStageId(),
        calulcatedResults.score
      );
      this.sceneController.switchScene(KEYS.StageComplete, {
        data: {
          onComplete: this.showWelcome,
          onCompleteScope: this,
          results: { ...calulcatedResults, isHighScore },
        },
      });
      this._appMode = GAME_MODES.STORY;
      return;
    }

    const calulcatedResults = this.model.calculateLevelResults(results);
    const isHighScore = this.player.registerScore(
      this.player.story.getCurrentStageId(),
      calulcatedResults.score
    );

    //const current = this.player.story.getCurrentLevel();
    const lvlComplete = this.player.story.completeCurrentLevelStage(calulcatedResults);
    // trigger stage complete scene
    this.sceneController.switchScene(KEYS.StageComplete, {
      data: {
        onComplete: () => {
          if (lvlComplete) {
            if (lvlComplete.cutsceneEnd) {
              this.showLevelInterstial(lvlComplete.cutsceneEnd);
            } else this.showLevelComplete();
          } else this.playCurrentStoryStage();
        },
        onCompleteScope: this,
        results: { ...calulcatedResults, isHighScore },
        heroid: this.player.activeHero || 'robin',
      },
    });

    this.player.save();
  }

  failStage(config) {
    this.setShortcuts(ShortcutController.STATES.DEFAULT);
    this.sceneController.showOverlayScene(KEYS.GameOver, {
      data: { heroid: this.player.activeHero || 'robin' },
    });
  }

  showLevelInterstial(interstitial) {
    this.sceneController.switchScene(KEYS.CutScene, {
      data: { type: interstitial, onComplete: this.showLevelComplete.bind(this) },
    });
  }

  showLevelComplete() {
    if (this.player.story?.isFinalStage()) this.showStoryComplete();
    else {
      this.sceneController.switchScene(KEYS.LevelComplete, {
        data: {
          heroid: this.player.activeHero || 'robin',
          nextLevel: this.player.story ? this.player.story.getNextLevelIndex() : 2,
        },
      });
    }
    this.player.save();
  }

  showStoryComplete() {
    this.sceneController.switchScene(KEYS.CutScene, {
      data: {
        type: 'outro',
        onComplete: (() => {
          this.sceneController.switchScene(KEYS.StoryComplete, {});
        }).bind(this),
      },
    });
  }

  showEnemyViewer() {
    this.sceneController.switchScene(KEYS.EnemyViewer, {});
  }

  // =============================================================================================
  // RETURN HOME
  // =============================================================================================

  returnToWelcome() {
    this.sceneController.showOverlayScene(KEYS.ConfirmAction, {
      data: {
        type: 'home',
        onConfirm: () => {
          if (this.paused) this.resumeGame();
          this.showWelcome();
        },
        onConfirmScope: this,
      },
    });
  }

  // =============================================================================================
  // PAUSE RESUME
  // =============================================================================================

  handleGamePaused() {
    // super.pauseGame();
    this.sceneController.startScene(KEYS.Pause);
    this.hud.setState('PAUSE');
  }

  resumeGame() {
    super.resumeGame();
    this.sceneController.stopScene(KEYS.Pause);
    this.hud.resumePreviousState();
  }

  toggleAudio() {
    this.settings.toggleAudio();
  }

  // =============================================================================================
  // HUD EVENTS
  // =============================================================================================

  _setHudEvents() {
    this.hud.setHudConfig(HudConfig);
    this.hud.events.on('START_GAME', this.showCharacterSelect, this);
    this.hud.events.on('RETURN_HOME', this.returnToWelcome, this);
    this.hud.events.on('CHARACTER_SELECT', this.showCharacterSelect, this);
    this.hud.events.on('REPLAY_STAGE', this.playCurrentStoryStage, this);
    this.hud.events.on('SHOW_UNLOCKS', this.showUnlocks, this);
    this.hud.events.on('PAUSE_GAME', this.pauseGame, this);
    this.hud.events.on('RESUME_GAME', this.resumeGame, this);
    this.hud.events.on('PLAY_STORY_STAGE', this.playCurrentStoryStage, this);
    this.hud.events.on('TOGGLE_SOUND', this.toggleAudio, this);
    this.hud.events.on('CLOSE_OVERLAY', this.closeOverlay, this);
  }

  closeOverlay() {
    this.sceneController.removeOverlayScene();
    this.hud.resumePreviousState();
  }

  // =============================================================================================
  // DEBUGGING
  // =============================================================================================

  _validateStoryStart() {
    if (!this.player.story || this.player.story.isComplete()) this.player.startNewStory();
    if (!this.player.activeHero) this.player.setActiveHero('robin');
  }

  setShortcuts(id) {
    this._shortcuts.setState(id);
  }

  // =============================================================================================
  // API LOAD
  // =============================================================================================

  loadFromCMS() {
    if (__WATCH__ || /.*dev-.*fb-app-hosting.com.*/.test(window.location.href)) {
      return LoadAPIData(
        'https://dev-ttggcms.fb-app-hosting.com/api/data',
        this.handleCmsLoad.bind(this),
        this.handleCmsFail.bind(this)
      );
    }
    this.assetsLoaded();
  }
  handleCmsLoad(data) {
    console.log('content loaded from cms', data);
    this.cache.json.remove('gameconfig');
    this.cache.json.add('gameconfig', data);
    this.assetsLoaded();
  }
  handleCmsFail() {
    this.assetsLoaded();
  }
}
